// import { filter, switchMap, take, debounceTime } from 'rxjs/operators';
import {
  MultiPointInstallationCreationAction,
  MultiPointInstallationCreationActionTypes,
  MultiPointInstallationCreationState,
} from '../../types/multi-point-installation-creation';
import {
  ResourceStatus,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
} from '../../types/resource';

import createRxStore from '..';

export const initialState: MultiPointInstallationCreationState = {
  open: false,
  setup: {
    name: '',
    height: '',
  },
  reorder: {
    positions: [],
  },

  resource: RESOURCE_IDLE, // Used to track response status of creation process
};

function reducer(
  state: MultiPointInstallationCreationState,
  action: MultiPointInstallationCreationAction,
): MultiPointInstallationCreationState {
  switch (action.type) {
    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_OPEN:
      return {
        ...initialState,
        open: true,
        reorder: {
          positions: action.sensors.map((s) => ({
            ...s,
            spacingRight: '',
          })),
        },
      };

    // ========================
    // "SETUP" Page
    // ========================
    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_SETUP_SET_NAME:
      return { ...state, setup: { ...state.setup, name: action.name } };
    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_SETUP_SET_HEIGHT:
      return { ...state, setup: { ...state.setup, height: action.height } };

    // ========================
    // "REORDER" Page
    // ========================
    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_SET_SENSOR_ORDER:
      return {
        ...state,
        reorder: {
          positions: action.serialNumbers
            .map((sn) =>
              state.reorder.positions.find((p) => p.serialNumber === sn),
            )
            .map((s, index) => ({
              ...s,
              spacingRight: state.reorder.positions[index].spacingRight,
            })),
        },
      };

    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_SET_SPACING_RIGHT:
      return {
        ...state,
        reorder: {
          positions: state.reorder.positions.map((i) => {
            if (i.serialNumber === action.serialNumber) {
              return { ...i, spacingRight: action.spacingRight };
            } else {
              return i;
            }
          }),
        },
      };

    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_REMOVE_SENSOR:
      return {
        ...state,
        reorder: {
          positions: state.reorder.positions.filter(
            (s) => s.serialNumber !== action.serialNumber,
          ),
        },
      };

    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_ADD_SENSORS: {
      const positions = [
        // Convert the previously-last item in the positions array to have a `spacingRight` value.
        ...state.reorder.positions.map((p) => ({
          ...p,
          spacingRight: p.spacingRight !== null ? p.spacingRight : '',
        })),

        // Add all the new values
        ...action.sensors.map((sensor) => ({
          ...sensor,
          spacingRight: '',
        })),
      ];

      // The final position doesn't have a spacingRight value.
      positions[positions.length - 1].spacingRight = null;

      return {
        ...state,
        reorder: { positions },
      };
    }

    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_LOADING:
      return { ...state, resource: RESOURCE_LOADING };

    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };

    case MultiPointInstallationCreationActionTypes.MULTI_POINT_INSTALLATION_CREATION_COMPLETE:
      return {
        ...state,
        resource: { status: ResourceStatus.COMPLETE, data: null },
      };

    default:
      return state;
  }
}

const MultiPointInstallationCreationStore = createRxStore(
  'MultiPointInstallationCreationStore',
  initialState,
  reducer,
);

export default MultiPointInstallationCreationStore;
